// src/services/createDraft.js

import { REGIONS } from "./regions.js";

const API_KEY = import.meta.env.VITE_SNOOOZ_API_KEY;
const USE_NGROK_HEADER = import.meta.env.VITE_USE_NGROK_HEADER === "true";

function buildHeaders(extra = {}) {
  return {
    ...extra,
    ...(USE_NGROK_HEADER ? { "ngrok-skip-browser-warning": "true" } : {})
  };
}

function getRegionFromSessionBundle(sessionBundle, fallbackRegion = "us") {
  const state = sessionBundle?.state || sessionBundle;
  return state?.region || fallbackRegion || "us";
}

function getSessionFromInput(sessionOrBundle) {
  const state = sessionOrBundle?.state || sessionOrBundle;

  if (state?.session) return state.session;

  return state || {};
}

function getTemplatesUrl(region = "us") {
  return (
    REGIONS[region]?.templatesUrl ||
    import.meta.env.VITE_SNOOOZ_TEMPLATES_URL ||
    ""
  );
}

export async function createEmailDraft(payload, sessionBundle) {
  const state = sessionBundle?.state || sessionBundle;
  const session = state?.session;
  const region = state?.region || "us";

  const createDraftUrl =
    REGIONS[region]?.createDraftUrl ||
    import.meta.env.VITE_SNOOOZ_CREATE_DRAFT_URL;

  if (!createDraftUrl) {
    throw new Error("Missing create draft URL.");
  }

  if (!session?.companyId || !session?.userId) {
    throw new Error("Missing Snoooz session. Please reconnect your account.");
  }

  console.log("Create draft request:", {
    createDraftUrl,
    region,
    companyId: session.companyId,
    userId: session.userId,
    payload
  });

  const res = await fetch(createDraftUrl, {
    method: "POST",
    headers: buildHeaders({
      "Content-Type": "application/json",
      "x-api-key": API_KEY,
      "x-user-token": session.companyId,
      "x-user-id": session.userId
    }),
    body: JSON.stringify(payload)
  });

  const data = await res.json().catch(() => ({}));

  console.log("Create draft response:", {
    ok: res.ok,
    status: res.status,
    data
  });

  if (!res.ok) {
    const message =
      data?.details ||
      data?.error ||
      data?.message ||
      `Draft creation failed with status ${res.status}.`;

    const err = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data?.draft;
}

export async function getExtensionTemplates(sessionOrBundle, regionOverride = "") {
  const region = regionOverride || getRegionFromSessionBundle(sessionOrBundle, "us");
  const session = getSessionFromInput(sessionOrBundle);

  const baseUrl = getTemplatesUrl(region);

  console.log("Templates preflight debug:", {
    region,
    baseUrl,
    hasApiKey: !!API_KEY,
    companyId: session?.companyId,
    userId: session?.userId
  });

  if (!baseUrl) {
    throw new Error(`Missing templates URL for region: ${region}`);
  }

  if (!session?.companyId || !session?.userId) {
    throw new Error("Missing companyId or userId for templates.");
  }

  const url = new URL(baseUrl);
  url.searchParams.set("apiKey", API_KEY);
  url.searchParams.set("companyId", session.companyId);
  url.searchParams.set("userId", session.userId);
  url.searchParams.set("t", String(Date.now()));

  console.log("Templates request URL:", url.toString());

  const res = await fetch(url.toString(), {
    method: "GET",
    cache: "no-store",
    headers: buildHeaders()
  });

  const text = await res.text();

  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }

  console.log("Templates response:", {
    ok: res.ok,
    status: res.status,
    data
  });

  if (!res.ok) {
    const err = new Error(
      data?.error ||
      data?.message ||
      data?.raw ||
      `Could not load templates. Status ${res.status}`
    );
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return Array.isArray(data?.templates) ? data.templates : [];
}

